import React, { Fragment, useState } from "react";
import { Container, Nav, NavDropdown, Navbar } from "react-bootstrap";
import { Link, NavLink, useNavigate } from "react-router-dom";

import logo from "../assets/images/ump_hub_logo.png";
import { INavLinkType } from "../types/shared";
import { useAppDispatch, useAppSelector } from "../hooks/reduxHooks";
import { AUTH_LEVELS } from "../lib/globals";
import { isXOrSmaller, validateToken } from "../lib/utils";
import { topBarLinks } from "./TopBar";
import { authActions } from "../features/auth/authSlice";
import useBreakpoint from "../hooks/useBreakpoint";

const Header = (): React.JSX.Element => {
    const [show, setShow] = useState<{ [key: string]: boolean }>({});
    const [navBarExpanded, setNavBarExpanded] = useState(false);
    const { user, loggedIn, token } = useAppSelector(state => state.auth);
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const breakpoint = useBreakpoint();

    const toggleDropdown = (title: string) => {
        setShow(prev => ({
            ...prev,
            [title]: !show[title],
        }));
    };

    const hideDropdown = (title: string) => {
        setShow(prev => ({
            ...prev,
            [title]: false,
        }));
    };

    const headerLinks: INavLinkType[] = [
        {
            title: "My Schedule",
            requireAuth: true,
            authLevels: AUTH_LEVELS.all,
            location: "#",
            children: [
                {
                    title: "Current Schedule",
                    location: "/my-schedule/current",
                },
                {
                    title: "Past Schedule",
                    location: "/my-schedule/past",
                },
            ],
        },
        {
            title: "View Open Games",
            location: "#",
            requireAuth: true,
            authLevels: AUTH_LEVELS.all,
            children: [
                {
                    title: "MAJORYBL Games",
                    location: "/games/majorybl",
                },
                {
                    title: "Wisconsin Baseball Tournaments",
                    location: "/games/wisconsin-baseball-tournaments",
                },
                {
                    title: "Illinois Baseball Tournaments",
                    location: "/games/illinois-baseball-tournaments",
                },
            ],
        },
        {
            title: "Rules",
            location: "#",
            children: [
                {
                    title: "MAJOR Youth Baseball League",
                    location: "/rules/majorybl",
                },
                {
                    title: "MAJOR High School League",
                    location: "/rules/majorhighschool",
                },
                {
                    title: "Wisconsin Baseball Tournaments",
                    location: "/rules/wisconsin-baseball-tournaments",
                },
                {
                    title: "Illinois Baseball Tournaments",
                    location: "/rules/illinois-baseball-tournaments",
                },
            ],
        },
        {
            title: "Umpire Registration",
            location: "/register",
        },
        {
            title: "Contact",
            location: "/contact",
        },
        {
            title: "My Profile",
            location: "/profile",
            requireAuth: true,
            authLevels: AUTH_LEVELS.all,
        },
        {
            title: "Admin",
            location: "#",
            requireAuth: true,
            authLevels: AUTH_LEVELS.sitemanagement,
            children: [
                {
                    title: "Current Year",
                    location: "",
                    header: true,
                },
                {
                    title: "MAJORYBL Games",
                    location: "/admin/current-year/majorybl",
                },
                {
                    title: "Wisconsin Baseball Tournaments",
                    location:
                        "/admin/current-year/wisconsin-baseball-tournaments",
                },
                {
                    title: "Illinois Baseball Tournaments",
                    location:
                        "/admin/current-year/illinois-baseball-tournaments",
                },
                {
                    title: "",
                    location: "",
                    divider: true,
                },
                {
                    title: "Past Year",
                    location: "",
                    header: true,
                },
                {
                    title: "MAJORYBL Games Archive",
                    location: "/admin/past-year/majorybl",
                },
                {
                    title: "Wisconsin Baseball Tournaments Archive",
                    location: "/admin/past-year/wisconsin-baseball-tournaments",
                },
                {
                    title: "Illinois Baseball Tournaments Archive",
                    location: "/admin/past-year/illinois-baseball-tournaments",
                },
                {
                    title: "",
                    location: "",
                    divider: true,
                },
                {
                    title: "Umpires",
                    location: "",
                    header: true,
                },
                {
                    title: "Pending Umpires",
                    location: "/admin/umpires/pending",
                },
                {
                    title: "Active Umpires",
                    location: "/admin/umpires/active",
                },
                {
                    title: "Inactive Umpires",
                    location: "/admin/umpires/inactive",
                },
                {
                    title: "Denied Umpires",
                    location: "/admin/umpires/denied",
                },
                {
                    title: "Umpire Ratings (Current Year)",
                    location: "/admin/umpires/ratings",
                },
                {
                    title: "Team Ban List",
                    location: "/admin/umpires/ban-list",
                },
                {
                    title: "Yearly Bonus Numbers",
                    location: "/admin/umpires/bonus",
                },
                {
                    title: "",
                    location: "",
                    divider: true,
                },
                {
                    title: "Site Settings",
                    location: "/admin/settings",
                },
                {
                    title: "Ejection Reports",
                    location: "/admin/ejection-reports",
                },
            ],
        },
    ];

    const buildChildren = (children: INavLinkType[]): React.ReactNode => {
        return children.map((child, idx) => {
            if (child.header) {
                return (
                    <NavDropdown.Header key={`${child.title}-${idx}`}>
                        {child.title}
                    </NavDropdown.Header>
                );
            } else if (child.divider) {
                return <NavDropdown.Divider key={`${child.title}-${idx}`} />;
            } else {
                return (
                    <NavDropdown.Item
                        as={Link}
                        to={child.location}
                        key={`${child.title}-${idx}`}
                        onClick={
                            isXOrSmaller("md", breakpoint)
                                ? toggleNavbar
                                : () => false
                        }
                    >
                        {child.title}
                    </NavDropdown.Item>
                );
            }
        });
    };

    const toggleNavbar = () => setNavBarExpanded(!navBarExpanded);

    const buildLinks = (links: INavLinkType[]) => {
        return links.map(
            (link: INavLinkType, idx) =>
                (!link.requireAuth ||
                    (link.requireAuth &&
                        loggedIn &&
                        user &&
                        token &&
                        validateToken(token) &&
                        link.authLevels?.includes(user.userLevel))) && (
                    <Fragment key={idx}>
                        {link.children ? (
                            <NavDropdown
                                title={link.title}
                                show={show[link.title]}
                                onClick={() => toggleDropdown(link.title)}
                                onMouseLeave={() => hideDropdown(link.title)}
                            >
                                {buildChildren(link.children)}
                            </NavDropdown>
                        ) : (
                            <Nav.Item>
                                <NavLink
                                    to={link.location}
                                    className="nav-link"
                                    role="headerNavLink"
                                    onClick={
                                        isXOrSmaller("md", breakpoint)
                                            ? toggleNavbar
                                            : () => false
                                    }
                                >
                                    {link.title}
                                </NavLink>
                            </Nav.Item>
                        )}
                    </Fragment>
                ),
        );
    };

    return (
        <div id="header">
            <Container fluid="lg" className="header" data-testid="header">
                <Navbar expand="lg" className="nav" expanded={navBarExpanded}>
                    <NavLink className="nav-item" to="/">
                        <img
                            src={logo}
                            alt="UmpHub.net Logo"
                            className="logo"
                        />
                    </NavLink>
                    <Navbar.Toggle
                        aria-controls="basic-navbar-nav"
                        onClick={toggleNavbar}
                    />
                    <Navbar.Collapse>
                        <Nav>
                            {navBarExpanded && (
                                <>
                                    <Nav.Item>
                                        <NavLink
                                            to="/"
                                            className="nav-link"
                                            role="headerNavLink"
                                            onClick={
                                                isXOrSmaller("md", breakpoint)
                                                    ? toggleNavbar
                                                    : () => false
                                            }
                                        >
                                            Home
                                        </NavLink>
                                    </Nav.Item>
                                </>
                            )}
                            {buildLinks(headerLinks)}
                            {navBarExpanded && (
                                <>
                                    <hr />
                                    {buildLinks(topBarLinks)}
                                    {user ? (
                                        <>
                                            <Nav.Item>
                                                <NavLink
                                                    to="/profile"
                                                    className="nav-link"
                                                    role="headerNavLink"
                                                    onClick={
                                                        isXOrSmaller(
                                                            "md",
                                                            breakpoint,
                                                        )
                                                            ? toggleNavbar
                                                            : () => false
                                                    }
                                                >
                                                    My Profile
                                                </NavLink>
                                            </Nav.Item>
                                            <Nav.Item>
                                                <Link
                                                    to="#"
                                                    className="nav-link"
                                                    role="headerNavLink"
                                                    onClick={() => {
                                                        toggleNavbar();
                                                        dispatch(
                                                            authActions.logout(),
                                                        );
                                                        navigate("/");
                                                    }}
                                                >
                                                    Logout
                                                </Link>
                                            </Nav.Item>
                                        </>
                                    ) : (
                                        <Nav.Item>
                                            <NavLink
                                                to="/login"
                                                className={"nav-link login"}
                                                role="login-link"
                                                onClick={
                                                    isXOrSmaller(
                                                        "md",
                                                        breakpoint,
                                                    )
                                                        ? toggleNavbar
                                                        : () => false
                                                }
                                            >
                                                Login
                                            </NavLink>
                                        </Nav.Item>
                                    )}
                                </>
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Navbar>
            </Container>
        </div>
    );
};

export default Header;
